import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { otherModesFor } from "../lib/roleModes";

/**
 * Comutatorul între rolurile aceluiași cont.
 *
 * For the coach who also trains in a squad, the parent who is also a
 * coordinator, the club admin who takes a team himself. One email, several
 * roles — which the data model always allowed and nothing in the interface
 * let you use.
 *
 * Renders nothing for the great majority of accounts, which hold one role. A
 * section headed "switch mode" with one entry in it would be furniture.
 *
 * The switch is a full page load on purpose. Every screen in the app reads
 * `activeRole` at render time and most of them hold fetched data keyed to it;
 * a soft navigation left the coach's team list on screen under the athlete's
 * chrome until something happened to refetch. A reload costs a second and is
 * always right.
 */
export default function RoleModeSwitcher({
  onNavigate,
  variant = "menu",
}: {
  /** Called before leaving, so a dropdown can close itself. */
  onNavigate?: () => void;
  /** "menu" = rows in a profile dropdown; "list" = standalone cards. */
  variant?: "menu" | "list";
}) {
  const { roles, activeRole, setActiveRole } = useAuth();
  const navigate = useNavigate();
  const modes = otherModesFor(roles, activeRole);

  if (modes.length === 0) return null;

  const go = (code: string, home: string) => {
    onNavigate?.();
    // Remembered, so the choice survives a refresh — see AuthContext.
    setActiveRole(code);
    // A hard navigation: see the note above. navigate() would leave the
    // previous role's data on screen.
    window.location.assign(home);
    // Fallback for environments where assign is stubbed (tests).
    if (process.env.NODE_ENV === "test") navigate(home);
  };

  if (variant === "list") {
    return (
      <div className="space-y-2">
        {modes.map((m) => (
          <button
            key={m.code}
            type="button"
            onClick={() => go(m.code, m.home)}
            className="flex items-center w-full gap-3 p-3 text-left transition-colors bg-white border shadow-sm rounded-2xl border-slate-200 hover:border-indigo-300"
          >
            <span className="flex items-center justify-center w-10 h-10 bg-indigo-100 shrink-0 rounded-xl">
              <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
              </svg>
            </span>
            <span className="min-w-0">
              <span className="block text-sm font-bold text-slate-800">{m.label}</span>
              <span className="block text-xs text-slate-500">Treci pe acest rol, cu același cont.</span>
            </span>
          </button>
        ))}
      </div>
    );
  }

  return (
    <div className="py-2 border-t border-slate-100">
      <p className="px-4 pb-1 text-[11px] font-bold tracking-wide uppercase text-slate-400">
        Schimbă modul
      </p>
      {modes.map((m) => (
        <button
          key={m.code}
          type="button"
          onClick={() => go(m.code, m.home)}
          className="flex items-center w-full gap-3 px-4 py-2.5 text-indigo-700 transition-colors hover:bg-indigo-50"
        >
          <svg className="w-5 h-5 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
          </svg>
          <span className="text-sm">{m.label}</span>
        </button>
      ))}
    </div>
  );
}
